"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { searchTools } from "@/lib/tools/registry";
import { cn } from "@/lib/utils";
import type { ToolRegistryItem } from "@/types/tools";
import { ToolCard } from "./tool-card";

type ToolSearchProps = {
  tools: ToolRegistryItem[];
  variant?: "compact" | "grid";
  placeholder?: string;
};

export function ToolSearch({
  tools,
  variant = "grid",
  placeholder = "Search tools, tags, or workflows...",
}: ToolSearchProps) {
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchTools(query, tools), [query, tools]);
  const compact = variant === "compact";

  return (
    <div className={cn("relative", compact ? "w-full" : "space-y-4")}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className="h-11 pl-9 pr-10"
          aria-label="Search tools"
        />
        {query ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1 h-9 w-9"
            aria-label="Clear search"
            onClick={() => setQuery("")}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {compact ? (
        query ? (
          <div className="absolute z-30 mt-2 max-h-96 w-full overflow-auto rounded-lg border border-zinc-200 bg-white p-2 shadow-xl">
            {results.length ? (
              results.slice(0, 8).map((tool) => (
                <Link
                  key={tool.id}
                  href={tool.route}
                  prefetch={false}
                  className="block rounded-md px-3 py-2 text-sm hover:bg-zinc-50"
                  onClick={() => setQuery("")}
                >
                  <span className="font-medium text-zinc-950">{tool.name}</span>
                  <span className="mt-0.5 block truncate text-xs text-zinc-500">
                    {tool.description}
                  </span>
                </Link>
              ))
            ) : (
              <p className="px-3 py-4 text-sm text-zinc-500">No matching tools yet.</p>
            )}
          </div>
        ) : null
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 text-sm text-zinc-500">
            <p>
              {results.length} {results.length === 1 ? "tool" : "tools"} found
            </p>
            {query ? <p>Searching: {query}</p> : null}
          </div>
          {results.length ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {results.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-white px-4 py-12 text-center">
              <p className="text-sm font-medium text-zinc-900">No tools match that search.</p>
              <p className="mt-1 text-sm text-zinc-500">
                Try a format, protocol, file type, or workflow keyword.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
